import { BoxProps, useColorModeValue, useToken } from '@chakra-ui/react'
import { useMemo } from 'react'
import { CartesianGrid, ComposedChart, Legend, Line, Tooltip } from 'recharts'

import { colors } from '../../../theme/colors'
import { CustomLegend } from '../StackedChart/customChartComponents/CustomLegend'
import { FloatingChartTooltipShell } from '../chartTooltip/ChartTooltipPointerContext'
import { FloatingCartesianChartTooltip } from '../chartTooltip/FloatingCartesianChartTooltip'
import type { FloatingCartesianExtraRow } from '../chartTooltip/FloatingCartesianChartTooltip'
import { CategoryAxisTick } from '../common/CategoryAxisTick'
import { RechartsWrapper } from '../common/RechartsWrapper'
import {
  CHART_GRID_STROKE,
  CHART_HEIGHT,
  CHART_MIN_WIDTH,
  DEFAULT_LINE_X_AXIS,
  MIN_WIDTH_PER_ITEM,
  SNAPSHOT_ANGLED_X_AXIS,
  SNAPSHOT_ANGLED_X_AXIS_HEIGHT,
  SNAPSHOT_CHART_HEIGHT,
  SNAPSHOT_CHART_MARGIN,
  SNAPSHOT_LINE_ACTIVE_DOT_R_PX,
  SNAPSHOT_LINE_ANGLED_X_AXIS_PADDING,
  SNAPSHOT_LINE_DOT_R_PX,
  SNAPSHOT_LINE_DOT_STROKE_PX,
} from '../common/chart.constants'
import { ChartData, ChartValue } from '../common/chart.types'
import { ChartGrid } from '../common/chartGridLines'
import { useCustomRecharts } from '../common/useCustomRecharts'

const X_AXIS_KEY = 'key'

const getSeriesDataKeys = (chartValues: ChartValue[] | undefined): string[] => {
  const first = chartValues?.[0] as Record<string, unknown> | undefined
  if (!first) return []
  return Object.keys(first).filter(
    k => k !== X_AXIS_KEY && typeof first[k] === 'number',
  )
}

/** Fallback when `lineSeries` is omitted: snake_case + camelCase → Title Words. */
const humanizeDataKey = (dataKey: string): string =>
  dataKey
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')

export type LineChartProps = BoxProps & {
  chartData: ChartData
  /** Number of Y-axis ticks. Default 5. Produces round values (e.g. 0, 5, 10, 15, 20). */
  yAxisTickCount?: number
  /** Override chart height (px). */
  chartHeight?: number
  /** Hide the built-in legend (render it externally instead). */
  hideLegend?: boolean
  /** Override the default line color palette. */
  lineColors?: string[]
  /**
   * When set (e.g. from CMS / electrum field labels), Recharts `name` and tooltips use `name`
   * instead of inferring from `dataKey`.
   */
  lineSeries?: { dataKey: string; name: string }[]
  /** Enable horizontal scrolling when there are many data points. */
  horizontalScroll?: boolean
  floatingTooltipExtraRows?: FloatingCartesianExtraRow[]
  /** Homepage snapshot charts: thinner line, smaller dots, tighter axis spacing. */
  variant?: 'default' | 'snapshot'
}

export const LineChart = ({
  chartData,
  yAxisTickCount = 5,
  chartHeight,
  hideLegend = false,
  lineColors: lineColorsProp,
  lineSeries,
  horizontalScroll = false,
  floatingTooltipExtraRows,
  variant = 'default',
  ...props
}: LineChartProps) => {
  const isSnapshot = variant === 'snapshot'
  const { XAxis, YAxis, chartWrapperRef } = useCustomRecharts({
    chartData,
    yAxisTickCount,
    series: lineSeries,
    variant,
  })

  const turquoise100 = useToken('colors.presets', 'turquoise.100')
  const activeDotStroke = useColorModeValue(turquoise100, 'white')

  const lines = useMemo(() => {
    if (lineSeries?.length) {
      return lineSeries
    }
    return getSeriesDataKeys(chartData.chartValues).map(dataKey => ({
      dataKey,
      name: humanizeDataKey(dataKey),
    }))
  }, [lineSeries, chartData.chartValues])

  /** Defaults match Figma Port-to-Port MoM: orange + magenta (pink.70), not blue/white. */
  const lineOrange = useToken('colors.presets', 'red.50')
  const lineMagenta = useToken('colors.presets', 'pink.70')
  const turquoise30 = useToken('colors.presets', 'turquoise.30')
  const blue80 = useToken('colors.presets', 'blue.80')
  const turquoise70 = useToken('colors.presets', 'turquoise.70')
  const defaultColors = useMemo(
    () => [lineOrange, lineMagenta, turquoise30, blue80, turquoise70],
    [lineOrange, lineMagenta, turquoise30, blue80, turquoise70],
  )
  const lineColors =
    lineColorsProp != null && lineColorsProp.length > 0
      ? lineColorsProp
      : defaultColors

  const floatingSeriesMeta = useMemo(
    () =>
      lines.map(({ dataKey, name }, index) => ({
        dataKey,
        name,
        color: lineColors[index % lineColors.length],
      })),
    [lineColors, lines],
  )

  const defaultDotProps = {
    stroke: 'white',
    strokeWidth: 3,
    r: 7,
  }
  const defaultActiveDotProps = {
    stroke: activeDotStroke,
    strokeWidth: 3,
    r: 9,
  }

  const snapshotDotProps = (seriesColor: string) => ({
    r: SNAPSHOT_LINE_DOT_R_PX,
    fill: seriesColor,
    stroke: colors.presets.neutral[0],
    strokeWidth: SNAPSHOT_LINE_DOT_STROKE_PX,
  })

  const snapshotActiveDotProps = (seriesColor: string) => ({
    r: SNAPSHOT_LINE_ACTIVE_DOT_R_PX,
    fill: seriesColor,
    stroke: colors.presets.neutral[0],
    strokeWidth: SNAPSHOT_LINE_DOT_STROKE_PX,
  })

  const itemCount = chartData.chartValues?.length ?? 0
  const scrollableWidth =
    horizontalScroll && itemCount > 0
      ? itemCount * MIN_WIDTH_PER_ITEM + 60
      : undefined

  const composed = (
    <ComposedChart
      data={chartData.chartValues}
      stackOffset="sign"
      margin={isSnapshot ? SNAPSHOT_CHART_MARGIN : undefined}
    >
      {XAxis({
        dataKey: chartData.nameKey ?? X_AXIS_KEY,
        ...(isSnapshot && { height: SNAPSHOT_ANGLED_X_AXIS_HEIGHT }),
        tick: (
          <CategoryAxisTick
            variant={variant}
            categoryAxisIsDate={chartData.categoryAxisIsDate}
            dateFormat={chartData.categoryAxisDateFormat}
            dateLayout={isSnapshot ? 'angled' : 'flat'}
            fill={isSnapshot ? colors.presets.neutral[0] : undefined}
            {...(isSnapshot
              ? {
                  unparseableTruncate: {
                    maxLength: 24,
                    angle: -30,
                    textAnchor: 'end',
                    fontWeight: 500,
                  },
                }
              : {
                  unparseableTruncate: {
                    maxLength: 24,
                  },
                })}
          />
        ),
        ...(isSnapshot
          ? {
              ...SNAPSHOT_ANGLED_X_AXIS,
              padding: SNAPSHOT_LINE_ANGLED_X_AXIS_PADDING,
            }
          : DEFAULT_LINE_X_AXIS),
      })}
      {YAxis({ padding: { top: 6, bottom: 0 }, tickMargin: 12 })}
      <CartesianGrid
        stroke={CHART_GRID_STROKE}
        horizontal={ChartGrid.horizontalLine}
        syncWithTicks
        vertical={false}
      />
      {!hideLegend && (
        <Legend
          content={<CustomLegend />}
          verticalAlign="top"
          wrapperStyle={{ paddingBottom: 10 }}
        />
      )}
      <Tooltip
        shared
        cursor={false}
        content={
          <FloatingCartesianChartTooltip
            floatingLayout="line"
            nameKey={chartData.nameKey ?? X_AXIS_KEY}
            series={floatingSeriesMeta}
            extraRows={floatingTooltipExtraRows}
            categoryAxisDateFormat={chartData.categoryAxisDateFormat}
            categoryAxisIsDate={chartData.categoryAxisIsDate}
            valueFormat={chartData.valueFormat}
          />
        }
      />
      {lines.map(({ dataKey, name }, index) => {
        const seriesColor = lineColors[index % lineColors.length]
        return (
          <Line
            key={dataKey}
            dataKey={dataKey}
            name={name}
            stroke={seriesColor}
            fill={seriesColor}
            dot={isSnapshot ? snapshotDotProps(seriesColor) : defaultDotProps}
            activeDot={
              isSnapshot
                ? snapshotActiveDotProps(seriesColor)
                : defaultActiveDotProps
            }
            strokeWidth={isSnapshot ? 2 : 4}
            isAnimationActive={false}
          />
        )
      })}
    </ComposedChart>
  )

  const resolvedChartHeight =
    chartHeight ?? (isSnapshot ? SNAPSHOT_CHART_HEIGHT : CHART_HEIGHT)

  const wrapperCommon = {
    ref: chartWrapperRef,
    yAxisLabel: chartData.yAxisLabel,
    xAxisLabel: chartData.xAxisLabel,
    scrollableWidth,
    chartHeight: resolvedChartHeight,
    minW: isSnapshot ? 0 : `${CHART_MIN_WIDTH}px`,
    variant,
    ...props,
  }

  return (
    <FloatingChartTooltipShell>
      {mouseHandlers => (
        <RechartsWrapper
          {...wrapperCommon}
          chartAreaMouseTracking={mouseHandlers}
        >
          {composed}
        </RechartsWrapper>
      )}
    </FloatingChartTooltipShell>
  )
}
